import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '../components';
import { Table } from '../components/table/table';
import { reaction, runInAction } from 'mobx';
import {
  StudioPageProps,
  useCoworkerFocusSubscription,
  useCrossTabNodeSync,
  useSolutionExistenceSubscription,
} from '../hooks';
import { WithLoadingState } from '../router';
import { NodeColumns, tableStore } from '../stores/table-store';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { useBobcatFlag } from '../hooks/use-bobcat-flag';
import { useClosedStages } from '../hooks/use-closed-stages';
import { ViewModeToggle } from './view-mode-toggle';
import { BuildTopbar } from './build-topbar';
import {
  buildViewModeAccess,
  defaultBuildViewMode,
  resolveEffectiveViewMode,
  type WorkflowsViewMode,
} from './workflows-view-mode';
import { TestView } from './test-view';
import { ValidateStage } from './validate/validate-stage';
import { CanvasView } from './canvas-view';
import { Stack } from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
} from '@pypestream/translations';
import { usePresenceMobXBridge } from '../hooks/use-presence-mobx-bridge';
import { StagePlaceholder } from './stage-placeholder';
import { SolutionOverview } from './solution-overview';
import { GuidedSetupCoach } from './guided-setup/guided-setup-coach';
import { DeployStage } from './deploy-stage';
import { ObserveStage } from './observe-stage';
import { MediaTab } from './files';
import { BuildActionsPanel } from './build-actions-panel';
import { BuildHealthPanel } from './build-health-panel';
import { SuggestAgentsPanel } from './canvas-view/suggest-agents-panel';
import { ExplainAgentPanel } from './canvas-view/explain-agent-panel';
import { resolveEffectiveStage } from '../stores/utils/workflow-stage';
import { SolutionAssistantDock } from '../components/solution-assistant/solution-assistant-dock';
import { solutionAssistantStore } from '../stores/solution-assistant-store';
import { BOBCAT_POLISH_CLASS } from '../utils/agent-palette';
import { useBuilderEntry } from '../hooks/use-builder-entry';
import { CollabConnectionAnnouncer, CollabConnectionBanner } from '../collab';
import { canFeedFromCollab } from '../collab/collab-live-source';
import { buildCollabUrl } from '../utils/collab-url';

type BuilderPageProps = StudioPageProps;

// Renders nothing — just runs the subscription outside the Table render tree.
// PSTUDIO-3953: `observer` because useCoworkerFocusSubscription reads
// `tableStore.solutionVersionId` and both re-subscribes AND asserts identity on
// it. Without it the read registers no dependency, so the value only refreshes
// when something else happens to re-render this component — and since the
// identity is now also what admits a coworker push, a lagging read makes the
// guard silently drop EVERY push instead of merely applying it late.
//
// To be precise about what changes: BuilderPage is itself an observer and reads
// the same field, so this child was already unmounted deterministically on a
// switch via the loader branch — a real, if indirect, dependency. What the
// wrapper buys is that the child no longer depends on the PARENT happening to
// re-render for a reason that has nothing to do with it. It also makes
// useSolutionExistenceSubscription reactive, which reads the same field here.
const CoworkerFocusSubscription = observer(() => {
  useCoworkerFocusSubscription();
  usePresenceMobXBridge();
  useSolutionExistenceSubscription(); // PSTUDIO-3671: cross-machine delete detect
  return null;
});

// PSTUDIO-4168: cross-tab node sync (PSTUDIO-3610) is mounted OUTSIDE the
// `isLoading` branch, unlike the subscriptions above.
//
// Its effect holds the whole protocol in closure state — `isDirty`, the 3s
// throttle's `lastReloadAt`, the in-flight flag and the 15s watchdog — and has
// an empty dep array, so unmounting it does not pause the protocol, it ERASES
// it. Sitting inside the branch, every publish erased it twice: a publish flips
// the table to `loading` on the way out (the version bump) and back on the way
// in. A sibling tab's broadcast landing in that window reached no subscriber at
// all, so the tab stayed on its pre-publish snapshot with nothing left to
// retry — and node numbers are allocated from exactly that local snapshot
// (`getExistingNodeNumbers`), which is how two tabs come to hand out the same
// one.
//
// Running it while the table loads is safe and is the point: `shouldReloadNodes`
// already refuses on `currentState === 'loading'`, and `reloadRetryDelayMs`
// classifies that as a block which clears with no event we listen for, so the
// hook keeps `isDirty` and re-checks on its own backoff until the table is
// ready. The signal is deferred instead of lost.
const CrossTabNodeSync = () => {
  useCrossTabNodeSync();
  return null;
};

const BuilderPage: React.FC<BuilderPageProps> = observer(() => {
  const {
    botName,
    draftVersion,
    customerId,
    botId,
    projectId,
    projectName,
    selectedBotName,
    solutionVersionId,
  } = useProStudioCtxSelector((ctx) => {
    return {
      botName: ctx.botName,
      customerId: ctx.customerId,
      projectId: ctx.project?.projectProStudioSetting?.id,
      botId:
        ctx.customerId && ctx.botName
          ? `${ctx.customerId}.${ctx.botName}`
          : undefined,
      deployedVersions: ctx.deployedVersions,
      draftVersion: ctx.draftVersion,
      projectName: ctx.project?.name,
      selectedBotName: ctx.selectedBot?.displayName,
      // See the note in use-table-store-sync: the key form is a property of the row.
      solutionVersionId: ctx.solutionVersionPsId ?? '',
    };
  });

  // Validate built from the testing tool's pieces. Off, the Validate stage
  // shows as closed (`useClosedStages`).
  const validateStageEnabled = useBobcatFlag('validateStage') === 'allowed';
  // `null` until something picks a view: then Build opens on the canvas when
  // it's available, otherwise the table (`defaultBuildViewMode`). Jumps to a
  // node or cell ask for the table through `pendingBuildViewMode`.
  const [viewMode, setViewMode] = useState<WorkflowsViewMode | null>(null);

  // Project Bobcat — Phase 1 of the 5-stage nav (`PrimaryNav`'s `StageNav`
  // writes `tableStore.activeStage`; this page reads it). With the flag off,
  // `resolveEffectiveStage` always returns 'build' regardless of what
  // `activeStage` holds, so this page renders exactly as it did before this
  // flag existed.
  const fiveStageNavEnabled = useBobcatFlag('fiveStageNav') === 'allowed';
  // Which Workflows views the flags open. Pure, so the binding is pinned by
  // `workflows-view-mode.test.ts`: the Test View tab beside Table View follows
  // Validate (PSTUDIO-4636) and not the master — the release page says that
  // flag also covers it, and the tab used to have a key of its own — while the
  // Build canvas and Files follow the master. `pending` and `denied` both fall
  // through to the table, so an un-answered flag never flashes a tab.
  const viewModeAccess = buildViewModeAccess({
    fiveStageNav: fiveStageNavEnabled,
    validateStage: validateStageEnabled,
  });
  const testViewEnabled = viewModeAccess.testView;
  const canvasViewEnabled = viewModeAccess.canvasView;
  // A stage its own flag keeps closed renders Build (PSTUDIO-4583).
  const closedStages = useClosedStages();
  const effectiveStage = resolveEffectiveStage(
    tableStore.activeStage,
    fiveStageNavEnabled,
    closedStages
  );

  // PSTUDIO-4449: the draft's live collab session and the table feed it
  // drives are held by the app shell (`CollabSessionHost`), not by this page,
  // so leaving Workflows for another page of the solution keeps the draft's
  // undo history, and every page of the solution has its rows.
  const collabUrl = buildCollabUrl(import.meta.env.FE_ADMIN_URL);
  // PSTUDIO-4450: the table reads from the document only when a session for
  // this draft can open — a legacy draft keeps loading from Hasura.
  const liveSource = canFeedFromCollab({
    url: collabUrl,
    solutionVersionId,
  });

  useEffect(() => {
    if (
      customerId &&
      botName &&
      draftVersion !== '' &&
      draftVersion !== undefined &&
      draftVersion !== null &&
      projectId !== null &&
      projectId !== '' &&
      botId
    ) {
      runInAction(() => {
        // PSTUDIO-4450: before the id, so the visit the id starts already
        // knows not to fetch from Hasura.
        tableStore.setLiveEditingSource(liveSource);
        tableStore.botId = botId;
        tableStore.solutionVersionId = solutionVersionId;
        if (projectId) {
          tableStore.projectProStudioSettingId = projectId;
        }
        tableStore.draftVersion = draftVersion;
      });

      // Project Bobcat: the conversational Create Solution flow marks this
      // right after creating a solution — this is the first point the route
      // has actually resolved to it (XState's `selectedBot.uniqueId`, which
      // the marker is matched against, isn't reliable before this). Only
      // ever matches the one navigation right after that flow; every other
      // solution load finds nothing pending and this is a no-op. See
      // `tableStore.pendingCanvasLanding`'s doc comment.
      if (tableStore.consumePendingCanvasLanding()) {
        setViewMode('canvas');
      }
      // The retired Files route redirects here with Build's Files tab asked for.
      const pendingBuildViewMode = tableStore.consumePendingBuildViewMode();
      if (pendingBuildViewMode) setViewMode(pendingBuildViewMode);
    }
  }, [
    solutionVersionId,
    customerId,
    botName,
    draftVersion,
    botId,
    projectId,
    liveSource,
  ]);

  // PSTUDIO-4240: the entrance the effect above cannot be. It can only ASSIGN,
  // and assigning an unchanged id is a no-op for the reaction that issues the
  // fetch — so a return into this page that never released the identity left a
  // store parked in the waiting state exactly as it found it.
  //
  // Declared AFTER the assignment, and that order is what makes a genuine
  // version switch refuse. Not by the id comparison — after the assignment the
  // two ids MATCH — but because the reaction runs synchronously on that
  // assignment and its forced cleanup lowers both waiting flags, so the rule's
  // second refusal is the one that fires. The hook's docblock names the lines;
  // `hooks/use-builder-entry` also owns the replay guard and is where the
  // wiring is tested.
  useBuilderEntry(tableStore, solutionVersionId);

  useEffect(() => {
    return () => {
      runInAction(() => {
        // Deliberately UNFORCED: a visit that is still legitimately waiting for
        // backend data must survive the departure. Forcing it here would drop
        // both waiting flags and unblock Publish for a version the backend has
        // not finished setting up — the direction PSTUDIO-4240 rejects. The
        // re-entry above is what re-establishes the data instead.
        tableStore.cleanupSolutionVersionSubscription(false);
      });
    };
  }, []); // Empty deps - only cleanup on unmount

  // The unsaved-changes guard has to live on the PAGE, not inside <Table>.
  // `table.tsx` registers its own `beforeunload` and removes it on unmount —
  // which was fine while the table was always mounted, but Test View/Canvas
  // replace it. Without this, a user could edit cells, switch views, close the
  // tab and lose the edits with no prompt. Duplicating the listener is
  // harmless: both read the same `hasUnsavedChanges` flag and the browser shows
  // one prompt, and leaving `table.tsx` untouched keeps this off shipped table
  // code.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (tableStore.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Treat the table as loading until tableStore points at the current
  // solution. Without this, switching solutions briefly shows the previous
  // solution's rows (the page-level loader hides as soon as XState reaches
  // idle, which is before tableStore has fetched the new workflow data).
  const tableMatchesCurrentSolution =
    !!customerId &&
    !!botName &&
    !!draftVersion &&
    tableStore.solutionVersionId === solutionVersionId;
  const isLoading =
    tableStore.currentState === 'loading' || !tableMatchesCurrentSolution;

  // PSTUDIO-4450 (WCAG 2.4.3): with live editing on, the loader carries the
  // connection banner and its Reconnect. When the sync it starts succeeds the
  // overlay unmounts with focus inside it, which drops focus to <body> just as
  // the table becomes usable. Land it on the first cell instead.
  const loaderHadFocus = useRef(false);
  useEffect(() => {
    if (isLoading || !loaderHadFocus.current) return;
    loaderHadFocus.current = false;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    tableStore.scrollToAndFocusCell(0, NodeColumns.nodeNumber);
  }, [isLoading]);

  // Resolve the requested mode against what the flags actually allow, once.
  // `viewMode` is component state and outlives a flag, so a user parked on a
  // gated view when its flag goes off — or a `pending` flag on a hard reload —
  // has to land somewhere valid rather than on a dead surface.
  const effectiveViewMode = resolveEffectiveViewMode(
    viewMode ?? defaultBuildViewMode({ canvasView: canvasViewEnabled }),
    viewModeAccess
  );

  // A jump to a node or cell made while Build is already showing (the header's
  // validation errors, a coworker's cursor) asks for the table the same way a
  // jump from another stage does — pick it up as soon as it's asked.
  useEffect(() => {
    if (effectiveStage !== 'build') return undefined;
    return reaction(
      () => tableStore.pendingBuildViewMode,
      (mode) => {
        if (!mode) return;
        const pending = tableStore.consumePendingBuildViewMode();
        if (pending) setViewMode(pending);
      }
    );
  }, [effectiveStage]);

  // The toggle is the same control in all views; only its placement differs.
  // Built here so the segment set and the rendered view are driven by the
  // SAME flags and cannot disagree — a segment that renders while the page
  // would bounce you back to the table is the bug this avoids.
  const viewModeToggle = (variant: 'floating' | 'toolbar') =>
    testViewEnabled || canvasViewEnabled ? (
      <ViewModeToggle
        variant={variant}
        value={effectiveViewMode}
        onChange={setViewMode}
        showTestView={testViewEnabled}
        showCanvasView={canvasViewEnabled}
      />
    ) : undefined;

  // With the 5-stage nav, testing lives in Validate, not in Build's tabs; a
  // Build view mode still parked on 'test' shows the table instead.
  const buildViewMode =
    effectiveViewMode === 'test' ? 'table' : effectiveViewMode;

  // Moving to another screen closes the node detail panel (the AI dock
  // stays). Skips the first render so a panel opened on arrival survives.
  const screenKey = `${effectiveStage}:${buildViewMode}`;
  const previousScreenKey = React.useRef(screenKey);
  useEffect(() => {
    if (!fiveStageNavEnabled || previousScreenKey.current === screenKey) return;
    previousScreenKey.current = screenKey;
    tableStore.setExpandedCellViewOpen(false);
    if (effectiveStage === 'build') {
      tableStore.consumePendingNodePanel();
      // A jump from another stage (Design's agent pills) asks for a Build view.
      const pendingBuildViewMode = tableStore.consumePendingBuildViewMode();
      if (pendingBuildViewMode) setViewMode(pendingBuildViewMode);
    }
  }, [screenKey, fiveStageNavEnabled, effectiveStage]);

  // Build polish (soft Use-case agent colors on the canvas and Table bands)
  // is scoped to the five-stage shell. A class on <html> rather than a
  // wrapper div, so the canvas, the table and anything they portal pick it up
  // without changing layout.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle(BOBCAT_POLISH_CLASS, fiveStageNavEnabled);
    return () => root.classList.remove(BOBCAT_POLISH_CLASS);
  }, [fiveStageNavEnabled]);

  const buildTopbar = (canvasControls?: React.ReactNode) => (
    <BuildTopbar
      value={buildViewMode}
      onChange={setViewMode}
      showCanvasView={canvasViewEnabled}
    >
      {canvasControls}
    </BuildTopbar>
  );

  const loader = (
    <div
      onFocus={() => {
        loaderHadFocus.current = true;
      }}
      onBlur={(e) => {
        // Moving to somewhere else clears it; a null target is also what
        // the overlay's own unmount reports, so that one is kept.
        const next = e.relatedTarget as Node | null;
        if (next && !e.currentTarget.contains(next)) {
          loaderHadFocus.current = false;
        }
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <Stack
        direction="column"
        gutter="medium"
        alignItems="center"
        style={{ textAlign: 'center' }}
      >
        {/* PSTUDIO-4241: silent on purpose. The region below is mounted
            permanently and already announces both edges of this state;
            letting the loader speak too would say "Loading workflows"
            twice and still never say it finished. */}
        <Loader relative announce={false} />
        {/* PSTUDIO-4450: a live draft's table waits for the first
            sync. The status and Reconnect have to be reachable while it
            waits — the table that normally draws them is not mounted. */}
        {liveSource && <CollabConnectionBanner />}
      </Stack>
    </div>
  );

  const stageContent =
    effectiveStage === 'design' ? (
      <>
        <CoworkerFocusSubscription />
        {/* Project Bobcat, 5-stage nav Phase 2 — Design's real content. Only
        reachable with fiveStageNavEnabled on, since resolveEffectiveStage
        forces 'build' otherwise. */}
        <SolutionOverview />
      </>
    ) : effectiveStage === 'deploy' ? (
      <>
        <CoworkerFocusSubscription />
        {/* Project Bobcat, 5-stage nav Phase 3 — Deploy reuses the real
        Versions page body and publish path. */}
        <DeployStage />
      </>
    ) : effectiveStage === 'observe' ? (
      <>
        <CoworkerFocusSubscription />
        <ObserveStage />
      </>
    ) : effectiveStage === 'validate' && validateStageEnabled ? (
      <>
        <CoworkerFocusSubscription />
        <ValidateStage />
      </>
    ) : effectiveStage !== 'build' ? (
      <>
        <CoworkerFocusSubscription />
        {/* Project Bobcat, 5-stage nav Phase 1 — Validate/Deploy/Observe don't
        have real content yet (see StagePlaceholder's own doc comment). Only
        reachable with fiveStageNavEnabled on, since resolveEffectiveStage
        forces 'build' otherwise. */}
        <StagePlaceholder stage={effectiveStage} />
      </>
    ) : fiveStageNavEnabled ? (
      <>
        <CoworkerFocusSubscription />
        {/* Project Bobcat, 5-stage nav: the Build stage's one bar (the
        prototype's `.topbar`) replaces the floating view-mode toggle, in
        normal flow above whichever view is showing. The canvas renders the
        bar itself so its zoom/find/error-routing controls can sit on it (they
        need the canvas's React Flow context). */}
        {buildViewMode === 'canvas' ? (
          <CanvasView renderTopbar={buildTopbar} />
        ) : (
          <div className="c-build-stage" data-testid="bobcat-build-stage">
            {buildTopbar()}
            <div className="c-build-stage__body">
              {buildViewMode === 'files' ? (
                <div className="c-build-stage__files">
                  <MediaTab />
                </div>
              ) : (
                <Table projectName={projectName} botName={selectedBotName} />
              )}
            </div>
          </div>
        )}
        {tableStore.actionsPanelOpen && <BuildActionsPanel />}
        {tableStore.healthPanelOpen && <BuildHealthPanel />}
        {tableStore.suggestAgentsPanelOpen && <SuggestAgentsPanel />}
        {tableStore.explainAgentId && <ExplainAgentPanel />}
      </>
    ) : (
      <>
        <CoworkerFocusSubscription />
        {/* Project Bobcat. Each tab is gated on its own flag; with both off (or
        while LaunchDarkly is still answering) this renders exactly what it
        always did — no toggle, the table.

        The mode is resolved ONCE against the flags rather than each branch
        re-checking its own, so a view whose flag is switched off cannot be
        left on screen and there is only one place to get the fallback right.

        The toggle is placed DIFFERENTLY per view, which is the fix for it
        overlapping the toolbar's auto-renumber button. Test View has no
        toolbar of its own, so it floats over the band. Table View hosts it
        INSIDE the toolbar, where it occupies real width and the toolbar's
        other controls reflow around it. */}
        {effectiveViewMode === 'test' && (
          <>
            {viewModeToggle('floating')}
            <TestView />
          </>
        )}
        {effectiveViewMode === 'canvas' && (
          <>
            {viewModeToggle('floating')}
            <CanvasView />
          </>
        )}
        {effectiveViewMode === 'table' && (
          <Table
            projectName={projectName}
            botName={selectedBotName}
            toolbarCenter={viewModeToggle('toolbar')}
          />
        )}
      </>
    );

  // CrossTabNodeSync and the status region stay mounted across the loading
  // flip (PSTUDIO-4168 / PSTUDIO-4240), whichever stage is showing.
  const pageChrome = (
    <>
      {/* PSTUDIO-4168: outside the branch — survives the loading flip a publish
          causes, so a sibling tab's broadcast is deferred, never lost. */}
      {/* PSTUDIO-4450: the collab document syncs tabs itself, and a reload
          from Hasura would overwrite its rows. */}
      {!liveSource && <CrossTabNodeSync />}
      {/* PSTUDIO-4240 (WCAG 2.1 AA, 4.1.3 Status Messages): mounted
          permanently so both "loading" and "loaded" are announced. The shared
          `Loader` announces by default (PSTUDIO-4241); this page passes
          `announce={false}` because this region already covers both edges. */}
      <div role="status" className="u-visually-hidden">
        <TranslationComponent
          i18nKey={
            (isLoading
              ? 'pro-studio/common:workflowsTableStatus.loading'
              : 'pro-studio/common:workflowsTableStatus.loaded') as TranslationKeys
          }
        />
      </div>
      {/* PSTUDIO-4449: the live-editing banner's voice, kept beside the region
          above for the same reason — the banner is drawn by the table, which
          the loading flip unmounts. On this page only: its words are about
          this workflow, and the session it reports on outlives the page. */}
      <CollabConnectionAnnouncer />
    </>
  );

  // The guided setup's coach, on every stage of a solution in setup.
  const setupCoach = !isLoading && (
    <GuidedSetupCoach
      onCanvas={effectiveStage === 'build' && buildViewMode === 'canvas'}
    />
  );

  if (isLoading || !fiveStageNavEnabled) {
    return (
      <>
        {pageChrome}
        {isLoading ? loader : stageContent}
        {setupCoach}
      </>
    );
  }

  // The assistant's dock sits beside the stage content, which is narrowed
  // to make room rather than drawn over.
  return (
    <>
      {pageChrome}
      <div
        className={`c-workflows-stage-host${
          !solutionAssistantStore.open
            ? ''
            : solutionAssistantStore.tab === 'agents'
              ? ' c-workflows-stage-host--with-agents'
              : ' c-workflows-stage-host--with-assistant'
        }`}
      >
        {stageContent}
      </div>
      {solutionAssistantStore.open && <SolutionAssistantDock />}
      {setupCoach}
    </>
  );
});

export const Builder = WithLoadingState(BuilderPage);
